import type { Lang } from './i18n.ts';
import { API_BASE } from './api.ts';
import { chooseEdgeVoice, isEdgeBuiltinFor } from './edge-voices.ts';
import { cachedTts, saveTts, ttsCacheKey } from './tts-cache.ts';

/**
 * 页面里念提醒（浏览器自带的 speechSynthesis）和响一声（WebAudio 合成两声，不用音频文件）。
 *
 * 浏览器规定：网页在他点过页面之前不许出声。所以第一次点击之前到来的提醒只弹、不响不念 —— 这是浏览器的限制，不是 bug。
 * 2026-10-30 起优先用服务器合成的 Edge 神经语音（speakSmart → /api/tts，音质最好），不通再退回浏览器自带的声音（Windows 本地语音），
 * 按「声音与语音」的「引擎」：自动（推荐）/ 只用 Edge / 只用浏览器自带。
 */

type Flat = Readonly<Record<string, unknown>>;

const num = (s: Flat, k: string, d: number, lo: number, hi: number): number => {
  const v = s[k];
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d;
};

/** 按界面语言挑一个声音（voiceFollow 开着时）；关着就挑第一个中文声音 */
export function pickVoice(voices: readonly SpeechSynthesisVoice[], lang: Lang, follow: boolean): SpeechSynthesisVoice | null {
  const want = follow ? (lang === 'en' ? ['en'] : lang === 'zh-HK' ? ['zh-HK', 'zh-TW', 'zh'] : ['zh-CN', 'zh']) : ['zh-CN', 'zh'];
  for (const w of want) {
    const v = voices.find(x => x.lang.replace('_', '-').toLowerCase().startsWith(w.toLowerCase()));
    if (v) return v;
  }
  return null;
}

/** 念一句。语速 ge_rate、音调 ge_vPitch 是 -50…50（百分比），音量 ge_vol 是 0…100 */
export function speak(text: string, s: Flat, lang: Lang): boolean {
  if (typeof window === 'undefined' || !('speechSynthesis' in window) || text.trim() === '') return false;
  const u = new SpeechSynthesisUtterance(text);
  u.volume = num(s, 'ge_vol', 90, 0, 100) / 100;
  u.rate = Math.max(0.5, 1 + num(s, 'ge_rate', 0, -50, 50) / 100);
  u.pitch = Math.max(0.1, 1 + num(s, 'ge_vPitch', 0, -50, 50) / 100);
  const v = pickVoice(window.speechSynthesis.getVoices(), lang, s['voiceFollow'] !== false);
  if (v) { u.voice = v; u.lang = v.lang; } else u.lang = lang === 'en' ? 'en-US' : lang;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
  return true;
}

/**
 * 提示音（ge_tone，2026-10-30）：叮咚（出厂）/ 警报 / 轻响 / 不响。和浏览器扩展里的那一份（apps/extension 的 tone.ts）是同一组音
 */
export type Tone = 'ding' | 'alarm' | 'soft';

const PATTERNS: Readonly<Record<Tone, ReadonlyArray<readonly [number, number, number]>>> = {
  ding: [[880, 0, 0.25], [660, 0.18, 0.25]],
  alarm: [[988, 0, 0.32], [740, 0.22, 0.32], [988, 0.44, 0.32], [740, 0.66, 0.32]],
  soft: [[660, 0, 0.12]]
};

/** 这一条要响哪种：选了「不响」或在全局静音时段里就是 null；叮咚遇到最紧急的换成警报 */
export function toneFor(s: Flat, urgent = false, now: Date = new Date()): Tone | null {
  const v = s['ge_tone'];
  const base: Tone | 'off' = v === 'alarm' || v === 'soft' || v === 'off' ? v : 'ding';
  if (base === 'off' || quietNow(s, now)) return null;
  return base === 'ding' && urgent ? 'alarm' : base;
}

/** 全局静音时段（ge_quiet / ge_qfrom / ge_qto），按他设的时区（tz）算。起止相同 = 全天 */
export function quietNow(s: Flat, now: Date = new Date()): boolean {
  if (s['ge_quiet'] !== true) return false;
  const hm = (k: string, d: string): number => {
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(typeof s[k] === 'string' ? (s[k] as string) : d);
    return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
  };
  const f = hm('ge_qfrom', '23:30');
  const t = hm('ge_qto', '07:30');
  if (f === t) return true;
  let n = now.getHours() * 60 + now.getMinutes();
  try {
    const tz = typeof s['tz'] === 'string' && s['tz'] !== '' ? (s['tz'] as string) : undefined;
    const parts = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, ...(tz ? { timeZone: tz } : {}) }).formatToParts(now);
    const h = Number(parts.find(p => p.type === 'hour')?.value ?? '0') % 24;
    const mi = Number(parts.find(p => p.type === 'minute')?.value ?? '0');
    n = h * 60 + mi;
  } catch { /* 时区认不出就用本机时间 */ }
  return f < t ? (n >= f && n < t) : (n >= f || n < t);
}

let ctx: AudioContext | null = null;

/** 放一种提示音。浏览器还没允许出声时什么都不做 */
export function playTone(tone: Tone): void {
  try {
    const C = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!C) return;
    ctx = ctx ?? new C();
    if (ctx.state === 'suspended') void ctx.resume();
    const t = ctx.currentTime;
    for (const [freq, at, gain] of PATTERNS[tone]) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = tone === 'alarm' ? 'square' : 'sine';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, t + at);
      g.gain.exponentialRampToValueAtTime(gain, t + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + at + 0.18);
      o.connect(g).connect(ctx.destination);
      o.start(t + at);
      o.stop(t + at + 0.2);
    }
  } catch { /* 出不了声就算了：弹窗照样在 */ }
}

/** 叮咚两声（老的入口，等于 playTone('ding')） */
export function beep(): void { playTone('ding'); }

/** 浏览器的声音清单第一次常常是空的（异步加载），等一下 voiceschanged，最多 1.5 秒 */
async function voicesReady(): Promise<SpeechSynthesisVoice[]> {
  const ss = window.speechSynthesis;
  const now = ss.getVoices();
  if (now.length > 0) return now;
  return new Promise(resolve => {
    const done = (): void => { ss.removeEventListener('voiceschanged', done); resolve(ss.getVoices()); };
    ss.addEventListener('voiceschanged', done);
    setTimeout(done, 1500);
  });
}

/**
 * 用 Edge 浏览器自带的神经语音念（2026-10-31）。和服务器合成的是同一套微软神经语音，在用户自己的浏览器里念，不经过我们的服务器。
 * 只有 Edge 浏览器有这套声音；别的浏览器、或者 4 秒还没开口（离线、被系统挡了），返回 false，交给下一种
 */
async function speakEdgeBuiltin(text: string, voiceId: string, s: Flat): Promise<boolean> {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return false;
  const v = (await voicesReady()).find(x => isEdgeBuiltinFor(x.name, x.lang, voiceId));
  if (!v) return false;
  const ss = window.speechSynthesis;
  const u = new SpeechSynthesisUtterance(text);
  u.voice = v;
  u.lang = v.lang;
  u.volume = num(s, 'ge_vol', 90, 0, 100) / 100;
  u.rate = Math.max(0.5, 1 + num(s, 'ge_rate', 0, -50, 50) / 100);
  u.pitch = Math.max(0.1, 1 + num(s, 'ge_vPitch', 0, -50, 50) / 100);
  ss.cancel();
  return new Promise(resolve => {
    let settled = false;
    const end = (ok: boolean): void => { if (!settled) { settled = true; clearTimeout(timer); if (!ok) ss.cancel(); resolve(ok); } };
    const timer = setTimeout(() => end(false), 4000);
    u.onstart = () => end(true);
    u.onerror = () => end(false);
    ss.speak(u);
  });
}

async function playMp3(blob: Blob, s: Flat): Promise<void> {
  const url = URL.createObjectURL(blob);
  const a = new Audio(url);
  a.volume = num(s, 'ge_vol', 90, 0, 100) / 100;
  a.onended = () => URL.revokeObjectURL(url);
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) window.speechSynthesis.cancel();
  await a.play();
}

/**
 * 念一句。按「引擎」（ge_vEngine）：
 *   自动（推荐）   Edge 浏览器自带的神经语音 → 本机缓存过的 → 服务器合成的 Edge 神经语音 → 浏览器自带的普通声音
 *   只用 Edge      前三种（都是同一套微软神经语音），都不行就不念
 *   只用浏览器自带  浏览器自带的普通声音
 * 前两种不经过我们的服务器（2026-10-31，老板：用户多了服务器带宽怎么办）。服务器那一种照旧保留：用 Chrome 的人靠它出好声音。
 * preview：「试听」时带上这一页还没保存的音色、语速、音调。返回实际用了哪一种（null = 一种都念不出来）
 */
export async function speakSmart(text: string, s: Flat, lang: Lang, preview?: { voice?: string; rate?: number; pitch?: number }): Promise<'edge' | 'browser' | null> {
  const t = text.trim().slice(0, 300);
  if (t === '') return null;
  const engine = s['ge_vEngine'] === 'browser' || s['ge_vEngine'] === 'server' ? s['ge_vEngine'] : 'auto';
  if (engine !== 'browser') {
    const rounded = (k: string): number => Math.round(num(s, k, 0, -50, 50));
    const voice = preview?.voice ?? chooseEdgeVoice(s, lang);
    const rate = preview?.rate ?? rounded('ge_rate');
    const pitch = preview?.pitch ?? rounded('ge_vPitch');
    const merged: Flat = { ...s, ge_rate: rate, ge_vPitch: pitch };
    if (await speakEdgeBuiltin(t, voice, merged)) return 'edge';
    const key = ttsCacheKey(voice, rate, pitch, t);
    try {
      const hit = await cachedTts(key);
      if (hit) { await playMp3(hit, s); return 'edge'; }
      const r = await fetch(API_BASE + '/tts', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'content-type': 'application/json', accept: 'audio/mpeg' },
        /* 音色、语速、音调都写明：服务器按这个合成，本机缓存才对得上 */
        body: JSON.stringify({ text: t, voice, rate, pitch })
      });
      if (r.ok) {
        const blob = await r.blob();
        void saveTts(key, blob);
        await playMp3(blob, s);
        return 'edge';
      }
    } catch { /* 连不上、浏览器不让出声：下面退回浏览器自带的 */ }
    if (engine === 'server') return null;
  }
  return speak(t, s, lang) ? 'browser' : null;
}

